/* eslint-disable class-methods-use-this */
class TextCustomizationController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
  }

  renderFontCarousel() {
    const {
      FONTS,
      selectionState,
      bodyTextureComposer,
      bottleSceneController,
    } = this.orchestrator;
    const { fontElCarousel } = this.orchestrator.dom;
    if (!fontElCarousel) return;

    fontElCarousel.innerHTML = FONTS.map((font, index) => {
      const blockedByInitials =
        selectionState.selectedTextLayout === 'initials' &&
        !font.applyLayoutInitials;
      const isActive =
        font.key === selectionState.selectedFontKey ||
        (!selectionState.selectedFontKey && index === 0);
      const fontWeights = { bold: '700', semibold: '600' };
      const weight = fontWeights[font.weight] || '400';
      const sample = selectionState.selectedTextLayout === 'initials' ? 'ABC' : 'Aa';

      return `
      <button
        type="button"
        class="options-device-font-carousel-item${isActive ? ' active' : ''}${
          blockedByInitials ? ' disabled' : ''
        }"
        data-font-key="${font.key}"
        data-font-index="${index}"
        title="${font.name}"
        style="font-family: '${font.name}', sans-serif; font-weight: ${weight};"
        ${blockedByInitials ? 'disabled' : ''}
      >
        <span class="font-sample">${sample}</span>
        <span class="font-name">${font.name}</span>
      </button>
    `;
    }).join('');

    const fontCarouselItems = fontElCarousel.querySelectorAll(
      '.options-device-font-carousel-item',
    );

    const scrollFontIntoView = item => {
      if (!item || typeof item.scrollIntoView !== 'function') return;
      const isSlideLayout = this.orchestrator.isCompactLayout(
        '(max-width: 1080px)',
      );
      if (!isSlideLayout) return;
      item.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    };

    fontCarouselItems.forEach(item => {
      item.addEventListener('click', () => {
        if (item.disabled || item.classList.contains('disabled')) return;
        if (item.classList.contains('active')) return;

        fontCarouselItems.forEach(option => option.classList.remove('active'));
        item.classList.add('active');
        selectionState.selectedFontKey = item.dataset.fontKey;
        selectionState.font = selectionState.selectedFontKey;
        selectionState.accepted = false;
        scrollFontIntoView(item);
        if (selectionState.customText.trim()) {
          bottleSceneController.applySelection();
        }
      });
    });

    const activeItem = fontElCarousel.querySelector(
      '.options-device-font-carousel-item.active',
    );
    if (activeItem) {
      requestAnimationFrame(() => scrollFontIntoView(activeItem));
    }

    FONTS.forEach(font => {
      bodyTextureComposer.ensureFontLoaded(font).then(ready => {
        if (!ready || !fontElCarousel) return;
        const item = fontElCarousel.querySelector(
          `[data-font-key="${font.key}"] .font-sample`,
        );
        if (item) item.style.fontFamily = `"${font.name}", sans-serif`;
      });
    });
  }

  ensureFontForLayout() {
    const { FONTS, selectionState } = this.orchestrator;
    const font = selectionState.getSelectedFont();
    if (
      selectionState.selectedTextLayout === 'initials' &&
      font &&
      !font.applyLayoutInitials
    ) {
      const fallback = FONTS.find(item => item.applyLayoutInitials) || FONTS[0];
      selectionState.selectedFontKey = fallback?.key || null;
    }
    this.renderFontCarousel();
  }

  setTextLayout(layout) {
    const {
      MAX_LENGTH_FULLNAME,
      MAX_LENGTH_INITIALS,
      selectionState,
      bottleSceneController,
    } =
      this.orchestrator;
    const { textInput, textLayoutButtons } = this.orchestrator.dom;

    const value = String(layout ?? 'fullname').toLowerCase();
    if (value !== 'initials' && value !== 'fullname') return;

    selectionState.selectedTextLayout = value;
    selectionState.layout = value;
    textLayoutButtons.forEach(button => {
      button.classList.toggle(
        'active',
        button.dataset.layout === selectionState.selectedTextLayout,
      );
    });

    if (textInput) {
      textInput.maxLength = value === 'initials'
        ? MAX_LENGTH_INITIALS
        : MAX_LENGTH_FULLNAME;
      if (textInput.value.length > textInput.maxLength) {
        textInput.value = textInput.value.slice(0, textInput.maxLength);
        selectionState.customText = textInput.value;
        selectionState.text = textInput.value;
      }
      const { contentTextCounter } = this.orchestrator.dom;
      if (contentTextCounter) {
        contentTextCounter.textContent = `${textInput.value.length}/${textInput.maxLength}`;
      }
    }

    this.ensureFontForLayout();
    if (
      selectionState.customText.trim() &&
      bottleSceneController.bodyMaterial
    ) {
      bottleSceneController.applySelection();
    }
  }

  clearTextIfArtIncompatible(
    art = this.orchestrator.selectionState.getSelectedArt(),
  ) {
    const { selectionState } = this.orchestrator;
    const { textInput } = this.orchestrator.dom;

    if (this.orchestrator.artAllowsText(art)) return false;

    selectionState.customText = '';
    if (textInput) textInput.value = '';
    return true;
  }

  syncTextLayoutOptions() {
    const { selectionState } = this.orchestrator;
    const {
      textsLayoutSection,
      textsFontSection,
      textsColorSectionDefault,
      textsColorSectionDual,
      textLayoutButtons,
      textInput,
    } = this.orchestrator.dom;
    const customConfig = this.orchestrator.getFontCustomConfig(
      selectionState.getSelectedArt()?.textCompatible,
    );
    const dualColors = customConfig?.colorUi === 'dual';

    if (textsLayoutSection) textsLayoutSection.hidden = true;
    if (textsFontSection) textsFontSection.hidden = Boolean(customConfig);
    if (textsColorSectionDefault) {
      textsColorSectionDefault.hidden =
        dualColors || selectionState.method === 'laser';
    }
    if (textsColorSectionDual) textsColorSectionDual.hidden = !dualColors;

    textLayoutButtons.forEach(button => {
      button.disabled = false;
      button.classList.remove('disabled');
    });

    if (
      selectionState.selectedTextLayout !== 'fullname' &&
      selectionState.selectedTextLayout !== 'initials'
    ) {
      this.setTextLayout('fullname');
    }
  }

  initializeFontCarousel() {
    const { selectionState } = this.orchestrator;
    const { fontElCarousel } = this.orchestrator.dom;
    if (selectionState.fontCarouselReady || !fontElCarousel) return;
    selectionState.fontCarouselReady = true;

    this.renderFontCarousel();
  }

  clampToUnitRange(value) {
    return Math.min(1, Math.max(0, value));
  }

  convertRgbToHex(red, green, blue) {
    const toHex = channel =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`.toUpperCase();
  }

  convertHexToRgb(hex) {
    const raw = String(hex || '')
      .trim()
      .replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    };
  }

  convertHsvToRgb(hueValue, saturation, value) {
    const hue = ((hueValue % 360) + 360) % 360;
    const chroma = value * saturation;
    const secondary = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
    const match = value - chroma;
    let red = 0;
    let green = 0;
    let blue = 0;

    if (hue < 60) {
      red = chroma;
      green = secondary;
    } else if (hue < 120) {
      red = secondary;
      green = chroma;
    } else if (hue < 180) {
      green = chroma;
      blue = secondary;
    } else if (hue < 240) {
      green = secondary;
      blue = chroma;
    } else if (hue < 300) {
      red = secondary;
      blue = chroma;
    } else {
      red = chroma;
      blue = secondary;
    }

    return {
      r: (red + match) * 255,
      g: (green + match) * 255,
      b: (blue + match) * 255,
    };
  }

  convertRgbToHsv(red, green, blue) {
    const normalizedRed = red / 255;
    const normalizedGreen = green / 255;
    const normalizedBlue = blue / 255;
    const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
    const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
    const delta = max - min;
    let hue = 0;

    if (delta !== 0) {
      if (max === normalizedRed) {
        hue = ((normalizedGreen - normalizedBlue) / delta) % 6;
      } else if (max === normalizedGreen) {
        hue = (normalizedBlue - normalizedRed) / delta + 2;
      } else {
        hue = (normalizedRed - normalizedGreen) / delta + 4;
      }
      hue *= 60;
      if (hue < 0) hue += 360;
    }

    return {
      h: hue,
      s: max === 0 ? 0 : delta / max,
      v: max,
    };
  }

  convertHsvToHex(hue, saturation, value) {
    const { r, g, b } = this.convertHsvToRgb(hue, saturation, value);
    return this.convertRgbToHex(r, g, b);
  }

  normalizeHexColor(hex) {
    const rgb = this.convertHexToRgb(hex);
    if (!rgb) return null;
    return this.convertRgbToHex(rgb.r, rgb.g, rgb.b);
  }

  getTextColorPresets() {
    return (
      this.orchestrator.TEXT_COLOR_PRESETS || [
        '#1E90FF',
        '#FF1493',
        '#FFD700',
        '#000000',
        '#FFFFFF',
      ]
    );
  }

  findTextColorPreset(hex) {
    const normalized = this.normalizeHexColor(hex);
    if (!normalized) return null;
    return (
      this.getTextColorPresets().find(
        preset => this.normalizeHexColor(preset) === normalized,
      ) || null
    );
  }

  syncTextColorSwatches(root, hexValue, { forceCustom = false } = {}) {
    if (!root) return;

    const swatches = root.querySelectorAll(
      '[data-role="swatches"] .texts-color-swatch',
    );
    const customPanel = root.querySelector('[data-role="custom-panel"]');
    const preset = forceCustom ? null : this.findTextColorPreset(hexValue);
    const isCustom = forceCustom || !preset;

    swatches.forEach(button => {
      const isActive = isCustom
        ? button.dataset.color === 'custom'
        : this.normalizeHexColor(button.dataset.color) ===
          this.normalizeHexColor(preset);
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (customPanel) {
      customPanel.hidden = !isCustom;
    }
  }

  bindHexColorPanel(root, { getColor, setColor }) {
    const { selectionState, bottleSceneController } = this.orchestrator;

    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const saturationValueArea = root.querySelector('[data-role="sv"]');
    const saturationValueCursor = root.querySelector('[data-role="sv-cursor"]');
    const hueInput = root.querySelector('[data-role="hue"]');
    const hexInput = root.querySelector('[data-role="hex"]');
    const preview = root.querySelector('[data-role="preview"]');
    const swatchButtons = root.querySelectorAll(
      '[data-role="swatches"] .texts-color-swatch',
    );
    let hsv = { h: 0, s: 0, v: 1 };
    let dragging = false;
    let customMode = !this.findTextColorPreset(getColor());

    const syncUi = (
      hexValue,
      { apply = false, forceCustom = customMode } = {},
    ) => {
      const rgb = this.convertHexToRgb(hexValue);
      if (!rgb) return;

      hsv = this.convertRgbToHsv(rgb.r, rgb.g, rgb.b);
      const normalized = this.convertRgbToHex(rgb.r, rgb.g, rgb.b);

      if (hueInput) hueInput.value = String(Math.round(hsv.h));
      if (hexInput) hexInput.value = normalized;
      if (preview) preview.style.backgroundColor = normalized;
      if (saturationValueArea) {
        saturationValueArea.style.background = `
        linear-gradient(to top, #000, transparent),
        linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))
      `;
      }
      if (saturationValueCursor) {
        saturationValueCursor.style.left = `${hsv.s * 100}%`;
        saturationValueCursor.style.top = `${(1 - hsv.v) * 100}%`;
      }

      customMode = forceCustom || !this.findTextColorPreset(normalized);
      this.syncTextColorSwatches(root, normalized, {
        forceCustom: customMode,
      });

      if (apply) {
        setColor(normalized);
        if (
          selectionState.customText.trim() &&
          bottleSceneController.bodyMaterial
        ) {
          bottleSceneController.applySelection();
        }
      }
    };

    const updateFromSaturationValue = (
      clientX,
      clientY,
      { apply = true } = {},
    ) => {
      if (!saturationValueArea) return;
      const rect = saturationValueArea.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const saturation = this.clampToUnitRange(
        (clientX - rect.left) / rect.width,
      );
      const value = this.clampToUnitRange(
        1 - (clientY - rect.top) / rect.height,
      );
      hsv = { ...hsv, s: saturation, v: value };
      syncUi(this.convertHsvToHex(hsv.h, saturation, value), {
        apply,
        forceCustom: true,
      });
    };

    syncUi(getColor(), { apply: false });

    swatchButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button.classList.contains('active')) {
          if (button.dataset.color === 'custom') {
            const customPanel = root.querySelector(
              '[data-role="custom-panel"]',
            );
            if (customPanel) customPanel.hidden = false;
          }
          return;
        }

        if (button.dataset.color === 'custom') {
          customMode = true;
          syncUi(getColor(), { apply: false, forceCustom: true });
          return;
        }

        customMode = false;
        syncUi(button.dataset.color, { apply: true, forceCustom: false });
      });
    });

    hueInput?.addEventListener('input', () => {
      hsv.h = Number(hueInput.value) || 0;
      syncUi(this.convertHsvToHex(hsv.h, hsv.s, hsv.v), {
        apply: true,
        forceCustom: true,
      });
    });

    hexInput?.addEventListener('change', () => {
      let value = hexInput.value.trim();
      if (!value.startsWith('#')) value = `#${value}`;
      if (!this.convertHexToRgb(value)) {
        hexInput.value = getColor();
        return;
      }
      syncUi(value, { apply: true, forceCustom: true });
    });

    saturationValueArea?.addEventListener('pointerdown', event => {
      dragging = true;
      saturationValueArea.setPointerCapture?.(event.pointerId);
      updateFromSaturationValue(event.clientX, event.clientY);
    });

    saturationValueArea?.addEventListener('pointermove', event => {
      if (!dragging) return;
      updateFromSaturationValue(event.clientX, event.clientY);
    });

    const endDrag = event => {
      if (!dragging) return;
      dragging = false;
      saturationValueArea?.releasePointerCapture?.(event.pointerId);
    };

    saturationValueArea?.addEventListener('pointerup', endDrag);
    saturationValueArea?.addEventListener('pointercancel', endDrag);
  }

  initializeDualTextColorPickers() {
    const { selectionState } = this.orchestrator;
    const { textsColorSectionDual } = this.orchestrator.dom;
    if (!textsColorSectionDual) return;

    const initialRoot = textsColorSectionDual.querySelector(
      '[data-dual-color="initial"]',
    );
    const nameRoot = textsColorSectionDual.querySelector(
      '[data-dual-color="name"]',
    );

    this.bindHexColorPanel(initialRoot, {
      getColor: () => selectionState.selectedInitialTextColor,
      setColor: value => {
        selectionState.selectedInitialTextColor = value;
        selectionState.accepted = false;
      },
    });

    this.bindHexColorPanel(nameRoot, {
      getColor: () => selectionState.selectedNameTextColor,
      setColor: value => {
        selectionState.selectedNameTextColor = value;
        selectionState.accepted = false;
      },
    });
  }

  handleTextInputChange() {
    const {
      TEXT_INPUT_DEBOUNCE_MS,
      selectionState,
      stepNavigationController,
      bottleSceneController,
    } = this.orchestrator;
    const { textInput } = this.orchestrator.dom;

    selectionState.customText = textInput.value || '';
    selectionState.text = selectionState.customText;
    selectionState.accepted = false;
    const { contentTextCounter } = this.orchestrator.dom;
    if (contentTextCounter) {
      contentTextCounter.textContent = `${selectionState.customText.length}/${textInput.maxLength}`;
    }

    stepNavigationController.syncStepNavAvailability();
    stepNavigationController.syncNextStepButton();
    if (
      !stepNavigationController.canNavigateToFinish() &&
      stepNavigationController.getActiveStepKey() === 'review'
    ) {
      stepNavigationController.setActiveStep('content');
    }

    clearTimeout(selectionState.textInputTimer);
    selectionState.textInputTimer = setTimeout(() => {
      if (!bottleSceneController.bodyMaterial) return;
      bottleSceneController.applySelection();
    }, TEXT_INPUT_DEBOUNCE_MS);
  }

  initializeTextControls() {
    const { MAX_LENGTH_FULLNAME, selectionState } = this.orchestrator;
    const {
      textInput,
      textLayoutButtons,
      textsColorSectionDefault,
      inkColorName,
      laserColorButtons,
      laserColorName,
    } =
      this.orchestrator.dom;

    if (!textInput || textInput.dataset.ready === 'true') return;
    textInput.dataset.ready = 'true';
    textInput.maxLength = MAX_LENGTH_FULLNAME;
    this.syncTextLayoutOptions();

    textInput.addEventListener('input', () => {
      this.handleTextInputChange();
    });

    textLayoutButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled || button.classList.contains('disabled')) return;
        this.setTextLayout(button.dataset.layout);
      });
    });

    this.bindHexColorPanel(textsColorSectionDefault, {
      getColor: () => selectionState.selectedTextColor,
      setColor: value => {
        selectionState.selectedTextColor = value;
        selectionState.setInkColor(value);
        if (inkColorName) inkColorName.textContent = this.getColorName(value);
      },
    });
    this.initializeDualTextColorPickers();

    laserColorButtons.forEach(button => {
      button.addEventListener('click', () => {
        selectionState.setLaserColor(button.dataset.laserColor);
        laserColorButtons.forEach(item => {
          const selected = item === button;
          item.classList.toggle('active', selected);
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
        if (laserColorName) {
          laserColorName.textContent = button.dataset.laserColor === 'dourado'
            ? 'Dourado'
            : 'Prata';
        }
        if (selectionState.customText.trim() && this.orchestrator.bottleSceneController.bodyMaterial) {
          this.orchestrator.bottleSceneController.applySelection();
        }
      });
    });
  }

  getColorName(hex) {
    const names = {
      '#1E90FF': 'Azul',
      '#FF1493': 'Rosa',
      '#FFD700': 'Amarelo',
      '#000000': 'Preto',
      '#FFFFFF': 'Branco',
    };
    return names[this.normalizeHexColor(hex)] || this.normalizeHexColor(hex) || hex;
  }
}

window.TextCustomizationController = TextCustomizationController;
